import React, {useEffect, useRef} from 'react'
import Text from '../../components/text/Text'
import VariableChipList from './VariableChipList'
import {ICaseVariable, IContentBlock, StyleToken} from './types'
import {escapeHtml, newParagraphBlock, parseStyleToken, buildStyleToken, styleTokenToClassName, uid, IStyleParts} from './utils'

interface IProps {
    className?: string
    blocks: IContentBlock[] // 연속된 paragraph 블록들
    onChange: (blocks: IContentBlock[]) => void
    variables?: ICaseVariable[] // 현재 카테고리의 기본 변수 셋
}

const ZWSP = '​' // 빈 텍스트(예: 줄바꿈 마커)도 커서가 멈출 수 있는 실제 글자가 있어야 해서 쓰는 폭 없는 공백

// 'block'(줄바꿈) 토큰이 있어도 에디터 화면에는 display:block을 반영하지 않는다({noBlock}) — 입력은 인라인으로 유지하고
// 줄바꿈은 data-style에만 조용히 보관해서, 미리보기/JSON에만 반영되게 한다.
const buildHtml = (blocks: IContentBlock[]): string => {
    if (!blocks?.length) return ''
    return blocks
        .map(block => {
            const text = escapeHtml(block.text || '') || '&#8203;'
            return `<span data-id="${block.id}" data-style="${block.style || ''}" class="${styleTokenToClassName(
                block.style,
                {noBlock: true},
            )}">${text}</span>`
        })
        .join('')
}

// 현재 DOM을 그대로 flat한 (id, text, style) 배열로 읽어온다 — 스타일 적용/변수 삽입 계산의 기준 데이터
const parseHtml = (container: HTMLElement): IContentBlock[] => {
    const blocks: IContentBlock[] = []
    container.childNodes.forEach(node => {
        if (node.nodeType === Node.TEXT_NODE) {
            const text = node.textContent || ''
            if (text) blocks.push({id: uid(), type: 'paragraph', text})
        } else if (node.nodeType === Node.ELEMENT_NODE) {
            const el = node as HTMLElement
            const rawText = el.textContent || ''
            const text = rawText === ZWSP ? '' : rawText
            if (!text) return
            const style = (el.getAttribute('data-style') || undefined) as StyleToken | undefined
            const existingId = el.getAttribute('data-id')
            blocks.push({id: existingId || uid(), type: 'paragraph', text, style})
        }
    })
    return blocks.length ? blocks : [newParagraphBlock()]
}

// Range의 경계(node, offset)를 에디터 전체 텍스트 기준 문자 인덱스로 변환
const getGlobalOffset = (container: HTMLElement, node: Node, offset: number): number => {
    let total = 0
    for (const child of Array.from(container.childNodes)) {
        const text = child.textContent || ''
        const innerTextNode = child.nodeType === Node.TEXT_NODE ? child : child.firstChild
        if (child === node) return total + (offset === 0 ? 0 : text.length)
        if (innerTextNode === node) return total + offset
        total += text.length
    }
    return total
}

// 에디터 전체 텍스트 기준 문자 인덱스에 커서를 놓는다. getGlobalOffset의 역방향.
// 변수 삽입 후 run이 인접 run과 병합돼 특정 span id가 사라져도(=id로는 못 찾음) 위치로 커서를 복원할 수 있게 한다.
const setCaretAtGlobalOffset = (container: HTMLElement, target: number) => {
    const selection = window.getSelection()
    if (!selection) return
    let remaining = target
    for (const child of Array.from(container.childNodes)) {
        const raw = child.textContent || ''
        const len = raw === ZWSP ? 0 : raw.length // 빈 블록의 ZWSP는 논리적으로 길이 0으로 취급
        const inner = child.nodeType === Node.TEXT_NODE ? child : child.firstChild
        if (remaining <= len) {
            const range = document.createRange()
            if (inner) range.setStart(inner, raw === ZWSP ? 0 : remaining)
            else range.setStart(child, 0)
            range.collapse(true)
            selection.removeAllRanges()
            selection.addRange(range)
            return
        }
        remaining -= len
    }
    // 못 찾으면 맨 끝
    const range = document.createRange()
    range.selectNodeContents(container)
    range.collapse(false)
    selection.removeAllRanges()
    selection.addRange(range)
}

// runs를 offset 지점에서 정확히 둘로 쪼갠다 (필요하면 run 하나를 잘라서 각각에 새 id 부여)
const splitRunsAt = (runs: IContentBlock[], offset: number): {before: IContentBlock[]; after: IContentBlock[]} => {
    const before: IContentBlock[] = []
    const after: IContentBlock[] = []
    let cursor = 0
    runs.forEach(run => {
        const text = run.text || ''
        const start = cursor
        const end = cursor + text.length
        cursor = end
        if (end <= offset) {
            before.push(run)
        } else if (start >= offset) {
            after.push(run)
        } else {
            const splitAt = offset - start
            before.push({...run, id: uid(), text: text.slice(0, splitAt)})
            after.push({...run, id: uid(), text: text.slice(splitAt)})
        }
    })
    return {before, after}
}

const splitRunsRange = (runs: IContentBlock[], start: number, end: number) => {
    const {before, after: rest} = splitRunsAt(runs, start)
    const {before: middle, after} = splitRunsAt(rest, end - start)
    return {before, middle, after}
}

// 인접한 run끼리 style이 같으면 하나로 합치고, 빈 텍스트 run은 제거
const mergeRuns = (runs: IContentBlock[]): IContentBlock[] => {
    const merged: IContentBlock[] = []
    runs.forEach(run => {
        if (!run.text) return
        const prev = merged[merged.length - 1]
        if (prev && prev.style === run.style) {
            prev.text = (prev.text || '') + run.text
        } else {
            merged.push({...run})
        }
    })
    return merged
}

// 크기 프리셋은 서로 배타적 — 하나를 켜면 나머지 크기는 끈다(bold/색/장식은 건드리지 않는다).
const applySize = (parts: IStyleParts, key: 'display' | 'title' | 'headline' | 'body' | 'caption') => {
    const on = !parts[key]
    parts.display = parts.title = parts.headline = parts.body = parts.caption = false
    parts[key] = on
}

const PART_TOGGLE_BUTTONS: {
    label: string
    className: string
    apply: (parts: IStyleParts) => void
}[] = [
    {label: 'Bold', className: 'font-bold', apply: parts => (parts.bold = !parts.bold)},
    {label: 'Display', className: 'text-[18px] font-bold', apply: parts => applySize(parts, 'display')},
    {label: 'Title', className: 'text-[16px] font-bold', apply: parts => applySize(parts, 'title')},
    {label: 'Headline', className: 'text-[15px] font-bold', apply: parts => applySize(parts, 'headline')},
    {label: 'Body', className: 'text-[14px]', apply: parts => applySize(parts, 'body')},
    {label: 'Caption', className: 'text-[12px]', apply: parts => applySize(parts, 'caption')},
    {
        label: '초록색',
        className: 'text-primary',
        apply: parts => (parts.color = parts.color === 'positive' ? undefined : 'positive'),
    },
    {
        label: '빨간색',
        className: 'text-font_red',
        apply: parts => (parts.color = parts.color === 'negative' ? undefined : 'negative'),
    },
    {label: '블러', className: '', apply: parts => (parts.blur = !parts.blur)},
    {label: '링크', className: 'underline', apply: parts => (parts.link = !parts.link)},
    {label: '줄바꿈', className: '', apply: parts => (parts.block = !parts.block)},
]

// 에디터 안 내용을 id 없이 (text/style)로만 직렬화. 리오더·머지 등 외부 변경 감지에 사용.
// id를 넣지 않는 이유: parseHtml이 타이핑 때마다 텍스트 노드에 새 id를 부여해서, id를 포함하면 매 입력마다 불일치로 잡혀 커서가 튄다.
const runSignature = (blocks: IContentBlock[]): string =>
    (blocks || []).map(block => `${block.text || ''}${block.style || ''}`).join('')

const RichParagraphRun: React.FC<IProps> = ({className, blocks, onChange, variables}) => {
    const editorRef = useRef<HTMLDivElement>(null)
    const savedRangeRef = useRef<Range | null>(null)

    // 최초 마운트 시 반드시 DOM을 채운다. 빈 블록도 data-id를 가진 span을 갖게 해서,
    // 첫 입력이 id 없는 텍스트 노드로 잡혀 새 id가 생기고 → key가 바뀌어 리마운트(포커스 손실)되는 것을 막는다.
    useEffect(() => {
        if (editorRef.current) editorRef.current.innerHTML = buildHtml(blocks)
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [])

    // 이후 순서 바꾸기/문단 런 병합처럼 외부에서 blocks 구성이 바뀐 경우에만 다시 그린다.
    // 사용자가 방금 타이핑한 내용(=현재 DOM)과 일치하면 건드리지 않아 커서/포커스가 유지된다.
    useEffect(() => {
        const el = editorRef.current
        if (!el) return
        if (runSignature(parseHtml(el)) !== runSignature(blocks)) {
            el.innerHTML = buildHtml(blocks)
        }
    })

    // 에디터 안에서 드래그/키보드로 선택이 바뀔 때마다 즉시 저장해둔다.
    // 버튼의 mousedown 시점에 저장하면, 그 시점엔 이미 포커스 이동으로 selection이 풀려있을 수 있어 너무 늦다.
    const saveSelection = () => {
        const el = editorRef.current
        const selection = window.getSelection()
        if (
            el &&
            selection &&
            selection.rangeCount > 0 &&
            el.contains(selection.getRangeAt(0).commonAncestorContainer)
        ) {
            savedRangeRef.current = selection.getRangeAt(0).cloneRange()
        } else {
            savedRangeRef.current = null
        }
    }

    const handleInput = () => {
        if (!editorRef.current) return
        onChange(parseHtml(editorRef.current))
    }

    // runs를 새로 계산한 뒤 DOM과 상위 state를 같은 데이터로 동시에 맞춘다 (리렌더/리마운트 타이밍에 기대지 않음)
    const rebuild = (nextRuns: IContentBlock[]) => {
        const el = editorRef.current
        const finalRuns = nextRuns.length ? nextRuns : [newParagraphBlock()]
        if (el) el.innerHTML = buildHtml(finalRuns)
        onChange(finalRuns)
        return finalRuns
    }

    const getSavedSelectionOffsets = (): {lo: number; hi: number} | null => {
        const el = editorRef.current
        const range = savedRangeRef.current
        if (!el || !range) return null
        if (!el.contains(range.commonAncestorContainer)) return null
        const start = getGlobalOffset(el, range.startContainer, range.startOffset)
        const end = getGlobalOffset(el, range.endContainer, range.endOffset)
        return {lo: Math.min(start, end), hi: Math.max(start, end)}
    }

    const replaceSelectionStyle = (computeNext: (current?: string) => string | undefined) => {
        const el = editorRef.current
        const offsets = getSavedSelectionOffsets()
        if (!el || !offsets || offsets.lo === offsets.hi) return

        const runs = parseHtml(el)
        const {before, middle, after} = splitRunsRange(runs, offsets.lo, offsets.hi)
        const styledMiddle = middle.map(run => ({...run, style: computeNext(run.style)}))
        rebuild(mergeRuns([...before, ...styledMiddle, ...after]))

        window.getSelection()?.removeAllRanges()
        savedRangeRef.current = null
    }

    const togglePart = (apply: (parts: IStyleParts) => void) => {
        replaceSelectionStyle(current => {
            const parts = parseStyleToken(current)
            apply(parts)
            return buildStyleToken(parts)
        })
    }

    const clearStyle = () => {
        replaceSelectionStyle(() => undefined)
    }

    // 저장된 커서/선택 위치에 변수 전용 run을 새로 끼워넣는다 (선택된 텍스트가 있으면 그 자리를 대체)
    const insertVariableToken = (name: string) => {
        const el = editorRef.current
        if (!el) return
        const runs = parseHtml(el)
        const offsets = getSavedSelectionOffsets()

        const token = `{${name}}`
        const varRun: IContentBlock = {id: uid(), type: 'paragraph', text: token}
        // 삽입 지점(글자 인덱스). 선택이 있으면 그 시작 위치를, 없으면 전체 끝에 붙인다.
        const insertionStart = offsets ? offsets.lo : runs.reduce((sum, run) => sum + (run.text?.length || 0), 0)
        let nextRuns: IContentBlock[]
        if (offsets) {
            const {before, after} = splitRunsRange(runs, offsets.lo, offsets.hi)
            nextRuns = mergeRuns([...before, varRun, ...after])
        } else {
            nextRuns = mergeRuns([...runs, varRun])
        }
        rebuild(nextRuns)
        savedRangeRef.current = null

        // 삽입한 토큰 바로 뒤에 커서를 놓는다. run 병합으로 span id가 사라져도 위치로 복원한다.
        el.focus()
        setCaretAtGlobalOffset(el, insertionStart + token.length)
    }

    return (
        <div className={`${className}`}>
            <div className={'flex items-center gap-x-[6px] flex-wrap mb-[8px]'}>
                {PART_TOGGLE_BUTTONS.map(button => (
                    <button
                        key={button.label}
                        type={'button'}
                        onMouseDown={e => e.preventDefault()}
                        onClick={() => togglePart(button.apply)}
                        className={`px-[10px] py-[4px] rounded-[5px] border border-line02 text-ti3 hover:border-primary ${button.className}`}>
                        {button.label}
                    </button>
                ))}
                <button
                    type={'button'}
                    onMouseDown={e => e.preventDefault()}
                    onClick={clearStyle}
                    className={'px-[10px] py-[4px] rounded-[5px] border border-line02 text-ti3 hover:border-primary'}>
                    스타일 해제
                </button>
            </div>

            <VariableChipList
                className={'mb-[8px]'}
                variables={variables}
                onSelect={insertVariableToken}
                onButtonMouseDown={e => e.preventDefault()}
            />

            <div
                ref={editorRef}
                contentEditable
                onInput={handleInput}
                onMouseUp={saveSelection}
                onKeyUp={saveSelection}
                suppressContentEditableWarning
                className={
                    'min-h-[60px] w-full border border-line02 rounded-[8px] px-[14px] py-[10px] bg-white text-body2 leading-[1.8] focus:outline-none focus:border-sub_blue'
                }
            />
            <Text className={'text-ti3 text-font03 mt-[6px]'}>
                텍스트를 드래그해서 선택한 뒤 위 버튼을 누르면 서식이 적용됩니다. 크기(Display/Title/Headline/Body/Caption)는
                하나만 적용되고, Bold·초록색·빨간색·블러·링크는 자유 조합됩니다. 연속된 조각은 기본적으로 한 줄로 이어붙고,
                "줄바꿈"을 적용한 조각만 그 앞에서 줄이 바뀝니다.
            </Text>
        </div>
    )
}

export default RichParagraphRun
